import React, { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Camera, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.gallery);
        setImages(response.data);
      } catch (err) {
        console.error('Failed to fetch gallery images:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  const categories = ['all', ...new Set(images.map(img => img.category))];
  const filteredImages = selectedCategory === 'all' ? images : images.filter(img => img.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Helmet><title>Galerie Photos - HPD</title></Helmet>
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Galerie Photos - Hope for Peace and Development</title>
        <meta name="description" content="Galerie photos de HPD: decouvrez en images nos actions sur le terrain." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">
              <Camera className="w-4 h-4 mr-2 inline" />Nos Actions en Images
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">Galerie Photos</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-3xl font-sans">Decouvrez en images l'impact de nos actions sur le terrain</p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`gallery-filter-${cat}`}
                className={`px-4 py-2 rounded-full text-sm font-medium font-sans transition-all ${
                  selectedCategory === cat ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'Toutes' : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid - Masonry style */}
      <section className="py-16" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length === 0 ? (
            <div className="text-center py-16"><p className="text-lg text-gray-500 font-sans">Aucune photo dans cette categorie</p></div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group cursor-pointer break-inside-avoid rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {image.category && (
                      <div className="absolute top-3 left-3 z-10">
                        <Badge className="bg-forest text-white text-xs">{image.category}</Badge>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="text-white font-serif font-bold text-lg">{image.title}</h3>
                      {image.description && <p className="text-white/80 text-sm font-sans mt-1">{image.description}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors" onClick={() => setSelectedImage(null)} data-testid="gallery-lightbox-close">
              <X className="w-8 h-8" />
            </button>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={selectedImage.image_url} alt={selectedImage.title} className="w-full max-h-[80vh] object-contain rounded-2xl" />
              <div className="text-center mt-4">
                <h3 className="text-white text-xl font-serif font-bold">{selectedImage.title}</h3>
                {selectedImage.description && <p className="text-gray-300 mt-2 font-sans text-sm">{selectedImage.description}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
